import { ChangeEvent, useCallback, useState } from 'react';

import { ZodError, ZodIssue, ZodSchema } from 'zod';

interface UseFormProps<T> {
  initialValues: T;
  validationSchema?: ZodSchema<T>;
  onSubmit: (values: T) => Promise<void>;
}

interface UseFormResult<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
  isValid: boolean;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleBlur: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  setFieldValue: (field: keyof T, value: any) => void;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;
  resetForm: () => void;
  setErrors: (errors: Partial<Record<keyof T, string>>) => void;
}

export function useForm<T extends Record<string, any>>({
  initialValues,
  validationSchema,
  onSubmit,
}: UseFormProps<T>): UseFormResult<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useCallback(
    (data: T): boolean => {
      if (!validationSchema) return true;

      try {
        validationSchema.parse(data);
        setErrors({});
        return true;
      } catch (error) {
        if (error instanceof ZodError) {
          // Cast to any to access errors property safely in this environment
          const zodError = error as any;
          const newErrors: Partial<Record<keyof T, string>> = {};
          if (zodError.errors) {
            zodError.errors.forEach((err: ZodIssue) => {
              if (err.path[0]) {
                newErrors[err.path[0] as keyof T] = err.message;
              }
            });
          }
          setErrors(newErrors);
        }
        return false;
      }
    },
    [validationSchema]
  );

  const validateField = useCallback(
    (name: keyof T, value: any) => {
      if (!validationSchema) return;

      try {
        // Create a partial schema for the single field
        const fieldSchema = (validationSchema as any).shape[name];
        if (fieldSchema) {
          fieldSchema.parse(value);
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }
      } catch (error) {
        if (error instanceof ZodError) {
          const zodError = error as any;
          if (zodError.errors && zodError.errors[0]) {
            setErrors(prev => ({
              ...prev,
              [name]: zodError.errors[0].message,
            }));
          }
        }
      }
    },
    [validationSchema]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setValues(prev => ({ ...prev, [name]: value }));

      if (touched[name as keyof T]) {
        validateField(name as keyof T, value);
      }
    },
    [touched, validateField]
  );

  const handleBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setTouched(prev => ({ ...prev, [name]: true }));
      validateField(name as keyof T, value);
    },
    [validateField]
  );

  const setFieldValue = useCallback(
    (field: keyof T, value: any) => {
      setValues(prev => ({ ...prev, [field]: value }));
      if (touched[field]) {
        validateField(field, value);
      }
    },
    [touched, validateField]
  );

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {} as Partial<Record<keyof T, boolean>>
    );
    setTouched(allTouched);

    if (validate(values)) {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid: Object.keys(errors).length === 0,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    resetForm,
    setErrors,
  };
}

export default useForm;
